
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AdminUser {
  isAdmin: boolean;
  loading: boolean;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
}

export function useAdminAuth(): AdminUser {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      const { data: userRole } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .single();

      setIsAdmin(!!userRole);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (password: string): Promise<boolean> => {
    try {
      setLoading(true);
      
      // Hent lagret passord fra system_settings
      const { data: setting } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_key', 'admin_password_hash')
        .single();

      if (!setting) {
        console.error('Admin password setting not found');
        return false;
      }

      // Enkelt passord-sammenligning for testing
      const isValidPassword = password === setting.setting_value;
      
      if (isValidPassword) {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          // Sjekk om bruker allerede har admin-rolle
          const { data: existingRole } = await supabase
            .from('user_roles')
            .select('id')
            .eq('user_id', user.id)
            .eq('role', 'admin')
            .maybeSingle();

          if (!existingRole) {
            // Opprett admin-rolle for denne brukeren
            const { error: insertError } = await supabase
              .from('user_roles')
              .insert({ user_id: user.id, role: 'admin' });

            if (insertError) {
              console.error('Error creating admin role:', insertError);
              return false;
            }
          }

          setIsAdmin(true);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Admin login error:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setIsAdmin(false);
  };

  return {
    isAdmin,
    loading,
    login,
    logout
  };
}
